import * as THREE from 'three';
import { DITHER_GLSL } from '../core/materials.js';

// ---------------------------------------------------------------------------
// The Zaan.
//
// The trick here is the depth texture: the terrain heightmap is uploaded as a
// single-channel texture, so the water shader knows how deep it is at every
// point. That gives shallows near the banks, a foam line exactly on the
// shore, and reeds that appear to stand in water rather than on it — all
// without a second render pass.
// ---------------------------------------------------------------------------

const VERT = /* glsl */ `
uniform float uTime;
uniform float uWaveHeight;
uniform float uWindDir;

varying vec2 vUvW;
varying vec3 vWorldPos;
varying float vWave;

void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);

    // Two crossed swells plus a fast ripple, measured along the wind rather
    // than along the world axes — otherwise the mills turn to a wind the sea
    // has plainly never heard of. Low frequency: this is a sheltered bay.
    float cw = cos(uWindDir);
    float sw = sin(uWindDir);
    float along = world.x * sw + world.z * cw;
    float across = world.x * cw - world.z * sw;

    float w =
        sin(along * 0.19 + uTime * 0.75) * 0.55 +
        sin(across * 0.23 - uTime * 0.55) * 0.45 +
        sin((along + across) * 0.51 + uTime * 1.35) * 0.22;

    world.y += w * uWaveHeight;

    vWave = w;
    vWorldPos = world.xyz;
    vUvW = uv;

    gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const FRAG = /* glsl */ `
uniform sampler2D uDepthMap;
uniform vec2 uMapOrigin;
uniform vec2 uMapScale;
uniform float uSeaLevel;
uniform float uTime;
uniform vec3 uShallow;
uniform vec3 uDeep;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uMoonDir;
uniform vec3 uMoonColor;
uniform vec3 uSkyColor;
uniform vec3 uHorizonColor;
uniform vec3 uFogColor;
uniform vec2 uFogRange;
uniform float uNight;
uniform float uShoalRadius;

varying vec2 vUvW;
varying vec3 vWorldPos;
varying float vWave;

${DITHER_GLSL}

// Terrain height in world units at a world-space xz position.
//
// Off the edge of the heightmap the answer is 0.0 — the same as an empty
// column inside it. Any other value (this used to say -40.0) paints the
// open sea a different shade from the seabed at the edge of the map, and
// the boundary between them shows up as a crisp rectangle drawn on the
// water: the grid, seen from outside. Matching the two makes the map edge
// unfindable, which is the whole point.
float terrainHeight(vec2 p) {
    vec2 uv = (p - uMapOrigin) / uMapScale;
    if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) return 0.0;
    return texture2D(uDepthMap, uv).r * 255.0;
}

// The shoal ring that closes off the sea room. There is no terrain this far
// out — the voxel grid ended long ago — so the seabed here is invented, which
// is enough to earn shallow water and a foam line exactly where the boat is
// stopped. Wobbled with the bearing so it does not read as a drawn circle.
float shoalHeight(vec2 p) {
    float r = length(p);
    float a = atan(p.y, p.x);
    float scale = uShoalRadius * 0.04;
    float wobble = (sin(a * 3.0) * 1.6 + sin(a * 7.0 + 1.1) * 0.9) * scale;
    float d = abs(r - (uShoalRadius + wobble));
    return (uSeaLevel - 0.35) - smoothstep(0.0, 7.0 * max(1.0, scale), d) * 9.0;
}

void main() {
    float bed = max(terrainHeight(vWorldPos.xz), shoalHeight(vWorldPos.xz));
    float depth = uSeaLevel - bed;

    // Colour by depth, quantised into a few bands for the voxel look.
    float d = clamp(depth / 7.0, 0.0, 1.0);
    d = bandDither(d, 5.0, gl_FragCoord.xy, 1.0);
    vec3 col = mix(uShallow, uDeep, d);

    // Sky reflection, strongest at grazing angles.
    vec3 viewDir = normalize(cameraPosition - vWorldPos);
    float fres = pow(1.0 - clamp(viewDir.y, 0.0, 1.0), 3.0);
    col = mix(col, mix(uHorizonColor, uSkyColor, 0.45), fres * 0.55);

    // Banded specular glitter off the swell.
    vec3 n = normalize(vec3(-vWave * 0.35, 1.0, -vWave * 0.28));
    float spec = pow(max(dot(reflect(-normalize(uSunDir), n), viewDir), 0.0), 24.0);
    col += uSunColor * bandDither(spec, 3.0, gl_FragCoord.xy, 1.2) * 0.85 * (1.0 - uNight);

    // A moon path on the water — the single best thing about a canal at night.
    float moonSpec = pow(max(dot(reflect(-normalize(uMoonDir), n), viewDir), 0.0), 42.0);
    col += uMoonColor * bandDither(moonSpec, 3.0, gl_FragCoord.xy, 1.2) * 1.15 * uNight;

    // Foam where the water runs out, animated so the shoreline breathes.
    float shore = 1.0 - smoothstep(0.0, 1.5, depth);
    float pulse = 0.5 + 0.5 * sin(uTime * 1.6 + vWorldPos.x * 0.6 + vWorldPos.z * 0.5);
    float foam = smoothstep(0.35, 0.95, shore * (0.65 + 0.5 * pulse));
    col = mix(col, vec3(0.92, 0.95, 0.96), foam * 0.85);

    // Fade out over dry land so the plane never shows above the dikes.
    float alpha = smoothstep(-0.15, 0.45, depth) * 0.93;

    float fog = smoothstep(uFogRange.x, uFogRange.y, length(cameraPosition - vWorldPos));
    col = mix(col, uFogColor, fog * 0.92);

    col *= mix(1.0, 0.42, uNight);

    gl_FragColor = vec4(col, alpha);
}
`;

/**
 * @param {Environment} env
 * @param {object} polder  from buildTerrain
 * @param {object} opts    { voxelSize, extent } — extent in world units
 */
/**
 * @param sea   { seaLevel, depth } — depth comes from buildWorldDepth, one
 *   texture holding every island's ground. Sampling each island's own map
 *   would mean a texture lookup per island per pixel; this is what lets the
 *   shallows and the foam line work at all of them rather than only at the
 *   one the map used to be built from.
 */
export function createWater(env, sea, { voxelSize, extent = 420, shoalRadius = 40 }) {
    // The plane must reach well past the shoals, or the sea simply stops short
    // of the edge of the world and you sail off the end of it.
    extent = Math.max(extent, shoalRadius * 2.6);
    const { seaLevel, depth } = sea;

    const depthMap = depth.texture;
    const worldSpan = depth.extent;
    const originX = depth.origin;
    const originZ = depth.origin;

    const material = new THREE.ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        transparent: true,
        depthWrite: false,
        uniforms: {
            uTime: env.uniforms.uTime,
            uSunDir: env.uniforms.uSunDir,
            uSunColor: env.uniforms.uSunColor,
            uMoonDir: env.uniforms.uMoonDir,
            uMoonColor: env.uniforms.uMoonColor,
            uSkyColor: env.uniforms.uSkyColor,
            uHorizonColor: env.uniforms.uHorizonColor,
            uFogColor: env.uniforms.uFogColor,
            uFogRange: env.uniforms.uFogRange,
            uNight: env.uniforms.uNight,
            uDepthMap: { value: depthMap },
            uMapOrigin: { value: new THREE.Vector2(originX, originZ) },
            uMapScale: { value: new THREE.Vector2(worldSpan, worldSpan) },
            uSeaLevel: { value: seaLevel },
            uShoalRadius: { value: shoalRadius },
            uWaveHeight: { value: 0.12 },
            uWindDir: { value: 0 },
            uShallow: { value: new THREE.Color(0x6f9c96).convertSRGBToLinear() },
            uDeep: { value: new THREE.Color(0x21414f).convertSRGBToLinear() },
        },
    });

    // Dense enough to carry the wave displacement, cheap enough not to care.
    // Segment count scales with the plane so the swell keeps its shape
    // instead of stretching into slow hills as the sea room grows.
    const segments = Math.min(420, Math.round(extent / 2.1));
    const geometry = new THREE.PlaneGeometry(extent, extent, segments, segments);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = seaLevel * voxelSize;
    mesh.renderOrder = 2;
    mesh.name = 'water';
    return mesh;
}
